/**
 * Unified Scanner Component
 * 
 * Single camera session for ISBN scanning + page scanning.
 * Eliminates the camera restart between phases that plagued the
 * separate BarcodeScanner and CameraScanner approach.
 * 
 * ARCHITECTURE:
 * - Single getUserMedia call at mount, camera stays active until unmount
 * - ISBN phase uses html5-qrcode with our camera stream
 * - Page phase uses native focus/stability detection + adjustable frame
 * - All phases share the same video element and stream
 * 
 * PHASES:
 * 1. initializing - Starting camera
 * 2. isbn-scanning - Looking for ISBN barcode (can skip)
 * 3. isbn-found - Brief display of found ISBN + metadata lookup
 * 4. frame-adjust - User adjusts capture frame (first time or on request)
 * 5. page-scanning - Auto-capture active
 * 6. captured - Brief green flash
 * 7. cooldown - Turn page period
 * 8. error - Something went wrong
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  X,
  Camera,
  BookOpen,
  Loader2,
  AlertCircle,
  CheckCircle,
  Keyboard,
  ArrowRight,
  Settings2,
} from 'lucide-react';
import { glassStyles, colors } from '@/lib/design';
import { isMediaDevicesSupported, isSecureContext, getMediaErrorDetails } from '@/lib/media';
import { useAutoCaptureDetection } from '@/lib/scan';
import { AdjustableFrame, FRAME_PRESETS, type FrameCorners, type FramePreset } from './AdjustableFrame';
import { MinimalFocusIndicator } from './FocusIndicator';

// Check for native BarcodeDetector support (instant ISBN scanning)
const hasNativeBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

// =============================================================================
// TYPES
// =============================================================================

export interface BookMetadata {
  isbn: string;
  title: string;
  author: string | null;
  coverImageUrl: string | null;
  publisher: string | null;
  pageCount: number | null;
  publishYear: number | null;
}

export interface BookInfo {
  title: string;
  author?: string | null;
  coverImageUrl?: string | null;
  totalPages?: number | null;
}

type ScannerPhase =
  | 'initializing'
  | 'isbn-scanning'
  | 'isbn-found'
  | 'manual-isbn'
  | 'frame-adjust'
  | 'page-scanning'
  | 'captured'
  | 'cooldown'
  | 'error';

export interface UnifiedScannerProps {
  /** Called when ISBN is detected/entered (triggers metadata lookup in parent) */
  onIsbnDetected: (isbn: string, metadata?: BookMetadata | null) => void;
  /** Called when user skips ISBN (goes to manual entry in parent) */
  onIsbnSkip: () => void;
  /** Called with captured page image */
  onCapture: (imageBase64: string, pageNumber: number) => Promise<void>;
  /** Called when user closes scanner */
  onClose: () => void;
  /** Called when scanning is complete */
  onComplete: () => void;
  /** Current page number */
  currentPage: number;
  /** Pages already scanned */
  scannedPages: number[];
  /** Whether parent is processing */
  isProcessing: boolean;
  /** Book info (if known - from ISBN lookup or existing book) */
  bookInfo?: BookInfo;
  /** Skip ISBN phase (e.g., for existing books) */
  skipIsbn?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CAPTURE_FLASH_MS = 300;
const COOLDOWN_MS = 1500;
const ISBN_SUCCESS_DISPLAY_MS = 1200; // Shortened for snappier UX

// =============================================================================
// COMPONENT
// =============================================================================

export const UnifiedScanner: React.FC<UnifiedScannerProps> = ({
  onIsbnDetected,
  onIsbnSkip,
  onCapture,
  onClose,
  onComplete,
  currentPage,
  scannedPages,
  isProcessing,
  bookInfo,
  skipIsbn = false,
}) => {
  // ==========================================================================
  // REFS
  // ==========================================================================
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const isMountedRef = useRef(true);
  const processingRef = useRef(false);
  
  // ==========================================================================
  // STATE
  // ==========================================================================
  
  const [phase, setPhase] = useState<ScannerPhase>('initializing');
  const [error, setError] = useState<string | null>(null);
  const [scannedIsbn, setScannedIsbn] = useState<string | null>(null);
  const [isbnMetadata, setIsbnMetadata] = useState<BookMetadata | null>(null);
  const [manualIsbn, setManualIsbn] = useState('');
  const [lastDetectedBarcode, setLastDetectedBarcode] = useState<string | null>(null);
  const [capturedPageNum, setCapturedPageNum] = useState<number | null>(null);
  const [usingFallbackScanner, setUsingFallbackScanner] = useState(false);
  const [fallbackScannerReady, setFallbackScannerReady] = useState(false);
  
  // Frame state
  const [frameCorners, setFrameCorners] = useState<FrameCorners>(
    FRAME_PRESETS.spread! // Default to spread for book scanning
  );
  const [framePreset, setFramePreset] = useState<FramePreset>('spread');
  const [showFramePresets, setShowFramePresets] = useState(false);
  
  // Video dimensions for frame
  const [videoDimensions, setVideoDimensions] = useState({ width: 1920, height: 1080 });
  
  // ==========================================================================
  // AUTO-CAPTURE DETECTION
  // ==========================================================================
  
  const autoCaptureEnabled = phase === 'page-scanning';
  
  const autoCapture = useAutoCaptureDetection(videoRef, {
    enabled: autoCaptureEnabled,
    minStableDurationMs: 500,
    onReadyToCapture: () => {
      if (phase === 'page-scanning' && !processingRef.current) {
        captureDocument();
      }
    },
  });
  
  // ==========================================================================
  // LOGGING
  // ==========================================================================
  
  const log = useCallback((msg: string) => {
    console.log(`[UnifiedScanner] ${msg}`);
  }, []);
  
  // ==========================================================================
  // HAPTIC FEEDBACK
  // ==========================================================================
  
  const hapticCapture = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 50, 30, 100]);
    }
  }, []);
  
  const hapticIsbnFound = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 50]);
    }
  }, []);
  
  // ==========================================================================
  // CLEANUP
  // ==========================================================================
  
  const cleanup = useCallback(() => {
    log('Cleaning up...');
    
    // Stop native barcode scanner
    if (nativeScanIntervalRef.current) {
      clearInterval(nativeScanIntervalRef.current);
      nativeScanIntervalRef.current = null;
    }
    barcodeDetectorRef.current = null;
    
    // Stop html5-qrcode if running
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (e) {
        // Ignore cleanup errors
      }
      html5QrCodeRef.current = null;
    }
    
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, [log]);
  
  // ==========================================================================
  // CAMERA INITIALIZATION
  // ==========================================================================
  
  const initializeCamera = useCallback(async () => {
    if (!isMediaDevicesSupported()) {
      const message = !isSecureContext()
        ? 'Camera requires HTTPS. Please use a secure connection.'
        : 'Your browser does not support camera access.';
      setError(message);
      setPhase('error');
      return;
    }
    
    try {
      log('Requesting camera...');
      
      // Wait one frame for React to commit the video element to DOM
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      const video = videoRef.current;
      if (!video) {
        throw new Error('Video element not found in DOM');
      }
      
      if (!isMountedRef.current) return;
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      
      if (!isMountedRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      
      streamRef.current = stream;
      video.srcObject = stream;
      
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Video timeout')), 5000);
        video.onloadedmetadata = () => {
          clearTimeout(timeout);
          video.play().then(resolve).catch(reject);
        };
        video.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Video error'));
        };
      });
      
      if (!isMountedRef.current) return;
      
      log(`Video ready: ${video.videoWidth}x${video.videoHeight}`);
      setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
      
      // Decide initial phase
      if (skipIsbn || bookInfo) {
        setPhase('page-scanning');
      } else {
        startIsbnScanning();
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        const errorDetails = getMediaErrorDetails(err);
        setError(errorDetails.message);
        setPhase('error');
      }
    }
  }, [log, skipIsbn, bookInfo]);
  
  // ==========================================================================
  // ISBN SCANNING - Native BarcodeDetector first, html5-qrcode fallback
  // ==========================================================================
  
  const nativeScanIntervalRef = useRef<number | null>(null);
  const barcodeDetectorRef = useRef<BarcodeDetector | null>(null);
  
  // Use ref for handleIsbnFound to avoid circular dependency in callbacks
  const handleIsbnFoundRef = useRef<(isbn: string) => void>(() => {});
  
  const stopIsbnScanning = useCallback(() => {
    // Stop native scanner
    if (nativeScanIntervalRef.current) {
      clearInterval(nativeScanIntervalRef.current);
      nativeScanIntervalRef.current = null;
    }
    barcodeDetectorRef.current = null;
    
    // Stop html5-qrcode fallback
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          html5QrCodeRef.current.stop();
        }
      } catch (e) {
        // Ignore
      }
    }
  }, []);
  
  const handleIsbnFound = useCallback(async (isbn: string) => {
    if (processingRef.current) return;
    processingRef.current = true;
    
    log(`✓ Valid ISBN: ${isbn}`);
    hapticIsbnFound();
    
    stopIsbnScanning();
    setScannedIsbn(isbn);
    setPhase('isbn-found');
    
    // Lookup metadata
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lookup-isbn`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isbn }),
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.book) {
          setIsbnMetadata(data.book);
          log(`Found: ${data.book.title}`);
        }
      }
    } catch (err) {
      log(`Metadata lookup failed: ${err}`);
    }
    
    // Auto-proceed to page scanning after brief display
    setTimeout(() => {
      if (isMountedRef.current) {
        onIsbnDetected(isbn, isbnMetadata);
        setPhase('page-scanning');
        processingRef.current = false;
      }
    }, ISBN_SUCCESS_DISPLAY_MS);
  }, [log, hapticIsbnFound, stopIsbnScanning, onIsbnDetected, isbnMetadata]);
  
  // Keep ref updated for use in interval callbacks
  handleIsbnFoundRef.current = handleIsbnFound;
  
  const startIsbnScanning = useCallback(async () => {
    setPhase('isbn-scanning');
    setUsingFallbackScanner(false);
    
    // Try native BarcodeDetector first (instant, no artifacts)
    if (hasNativeBarcodeDetector && videoRef.current) {
      try {
        const detector = new BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e']
        });
        barcodeDetectorRef.current = detector;
        
        // Scan at 30fps for instant detection
        nativeScanIntervalRef.current = window.setInterval(async () => {
          if (!barcodeDetectorRef.current || !videoRef.current || processingRef.current) return;
          
          try {
            const barcodes = await barcodeDetectorRef.current.detect(videoRef.current);
            if (barcodes.length > 0) {
              const isbn = barcodes[0].rawValue;
              log(`Native barcode detected: ${isbn}`);
              setLastDetectedBarcode(isbn);
              
              // Validate ISBN format (10 or 13 digits only, not 12-digit UPC)
              const cleanIsbn = isbn.replace(/[-\s]/g, '');
              if (/^(\d{10}|\d{13})$/.test(cleanIsbn) || /^\d{9}X$/i.test(cleanIsbn)) {
                // Stop scanning before processing
                if (nativeScanIntervalRef.current) {
                  clearInterval(nativeScanIntervalRef.current);
                  nativeScanIntervalRef.current = null;
                }
                barcodeDetectorRef.current = null;
                handleIsbnFoundRef.current(cleanIsbn);
              }
            }
          } catch (e) {
            // Detection failed this frame - expected, continue
          }
        }, 33); // ~30fps
        
        log('Native ISBN scanning active');
        return;
      } catch (err: any) {
        log(`Native scanner not available: ${err.message}`);
        // Fall through to html5-qrcode
      }
    }
    
    // Fallback to html5-qrcode (via state change + useEffect)
    log('Using html5-qrcode fallback, triggering init...');
    setUsingFallbackScanner(true);
    setFallbackScannerReady(true);
  }, [log]);

  // Initialize fallback scanner AFTER React renders the visible container
  useEffect(() => {
    if (!fallbackScannerReady || !usingFallbackScanner || phase !== 'isbn-scanning') return;
    
    // Reset trigger
    setFallbackScannerReady(false);
    
    const initFallbackScanner = async () => {
      const scannerId = 'isbn-scanner-region';
      const container = document.getElementById(scannerId);
      
      if (!container) {
        log('Fallback container not found after render');
        setError('Scanner initialization failed');
        setPhase('error');
        return;
      }
      
      try {
        const html5QrCode = new Html5Qrcode(scannerId, { verbose: false });
        html5QrCodeRef.current = html5QrCode;
        
        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 30,
            qrbox: { width: 280, height: 140 },
            disableFlip: true,
          } as any,
          (decodedText) => {
            log(`Fallback barcode detected: ${decodedText}`);
            setLastDetectedBarcode(decodedText);
            
            // Validate ISBN format (10 or 13 digits only, not 12-digit UPC)
            const cleanIsbn = decodedText.replace(/[-\s]/g, '');
            if (/^(\d{10}|\d{13})$/.test(cleanIsbn) || /^\d{9}X$/i.test(cleanIsbn)) {
              handleIsbnFoundRef.current(cleanIsbn);
            } else {
              log(`Ignoring non-ISBN barcode: ${decodedText}`);
            }
          },
          () => {}
        );
        
        log('Fallback ISBN scanning active');
      } catch (err: any) {
        log(`Fallback scanner error: ${err.message}`);
        setError('Could not start barcode scanner');
        setPhase('error');
      }
    };

    initFallbackScanner();
  }, [fallbackScannerReady, usingFallbackScanner, phase, log]);
  
  const handleManualIsbnSubmit = useCallback(() => {
    const cleaned = manualIsbn
      .toUpperCase()
      .replace(/^ISBN[-:\s]*(10|13)?[-:\s]*/i, '')
      .replace(/[-\s]/g, '');
    
    const isValidIsbn10 = /^\d{9}[\dX]$/.test(cleaned);
    const isValidIsbn13 = /^\d{13}$/.test(cleaned);
    
    if (isValidIsbn10 || isValidIsbn13) {
      handleIsbnFound(cleaned);
    } else {
      setError('Invalid ISBN. Enter 10 or 13 digits (ISBN-10 may end in X).');
    }
  }, [manualIsbn, handleIsbnFound]);
  
  // ==========================================================================
  // PAGE CAPTURE
  // ==========================================================================
  
  const captureDocument = useCallback(async () => {
    if (phase !== 'page-scanning' || processingRef.current) {
      log(`Capture blocked - phase: ${phase}, processing: ${processingRef.current}`);
      return;
    }
    
    processingRef.current = true;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas) {
      processingRef.current = false;
      return;
    }
    
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context unavailable');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);
      
      const pageNum = currentPage;
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.90).split(',')[1];
      
      hapticCapture();
      log(`✓ Page ${pageNum} captured`);
      
      setCapturedPageNum(pageNum);
      setPhase('captured');
      
      // Notify parent
      await onCapture(imageBase64, pageNum);
      
      // Start cooldown
      autoCapture.startCooldown(COOLDOWN_MS);
      
      // Transition through phases
      setTimeout(() => {
        if (!isMountedRef.current) return;
        setPhase('cooldown');
        
        setTimeout(() => {
          if (!isMountedRef.current) return;
          setPhase('page-scanning');
          processingRef.current = false;
          log('📷 Ready for next page');
        }, COOLDOWN_MS - CAPTURE_FLASH_MS);
      }, CAPTURE_FLASH_MS);
      
    } catch (err: any) {
      log(`Capture error: ${err.message}`);
      processingRef.current = false;
      if (isMountedRef.current) {
        setPhase('page-scanning');
      }
    }
  }, [phase, currentPage, hapticCapture, log, onCapture, autoCapture]);
  
  const handleManualCapture = useCallback(() => {
    captureDocument();
  }, [captureDocument]);
  
  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================
  
  useEffect(() => {
    isMountedRef.current = true;
    initializeCamera();
    
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, []);
  
  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  
  const handleClose = useCallback(() => {
    cleanup();
    if (scannedPages.length > 0) {
      onComplete();
    } else {
      onClose();
    }
  }, [cleanup, scannedPages.length, onComplete, onClose]);
  
  const handleDone = useCallback(() => {
    cleanup();
    onComplete();
  }, [cleanup, onComplete]);
  
  const handleRetry = useCallback(() => {
    setError(null);
    cleanup();
    setTimeout(() => {
      if (isMountedRef.current) {
        initializeCamera();
      }
    }, 300);
  }, [cleanup, initializeCamera]);
  
  const handleSkipIsbn = useCallback(() => {
    stopIsbnScanning();
    onIsbnSkip();
  }, [stopIsbnScanning, onIsbnSkip]);
  
  // ==========================================================================
  // RENDER: UNIFIED STRUCTURE
  // Video element MUST always be in DOM for camera init to work
  // ==========================================================================
  
  const isIsbnPhase = phase === 'isbn-scanning';
  const isPagePhase = ['page-scanning', 'captured', 'cooldown'].includes(phase);
  const showVideoFeed = !['error', 'isbn-found', 'manual-isbn'].includes(phase);
  
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* 
        CRITICAL: Video element MUST always be in DOM
        Camera initialization needs the ref to attach the stream.
        We hide it visually for certain phases but never remove it.
      */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover ${showVideoFeed ? '' : 'invisible'}`}
        playsInline
        muted
        autoPlay
      />
      
      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* ================================================================== */}
      {/* ERROR STATE */}
      {/* ================================================================== */}
      {phase === 'error' && (
        <div className="absolute inset-0 z-[200] flex items-center justify-center bg-black/90">
          <div className="text-center p-8 rounded-3xl max-w-sm mx-4" style={glassStyles.card}>
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-white font-bold text-xl mb-2">Camera Error</h3>
            <p className="text-white/70 mb-6">{error}</p>
            <div className="flex gap-3">
              <button
                onClick={handleRetry}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-medium"
              >
                Try Again
              </button>
              <button
                onClick={onClose}
                className="py-3 px-4 bg-gray-700 text-white rounded-xl"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* ================================================================== */}
      {/* INITIALIZING STATE */}
      {/* ================================================================== */}
      {phase === 'initializing' && (
        <div className="absolute inset-0 z-[200] flex items-center justify-center bg-black">
          <div className="text-center p-8 rounded-3xl" style={glassStyles.card}>
            <Loader2 className="w-12 h-12 text-indigo-400 mx-auto animate-spin mb-4" />
            <p className="text-white text-lg font-medium">Starting camera...</p>
          </div>
        </div>
      )}
      
      {/* ================================================================== */}
      {/* ISBN FOUND SUCCESS STATE */}
      {/* ================================================================== */}
      {phase === 'isbn-found' && (
        <div className="absolute inset-0 z-[200] flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-teal-50">
          <div className="text-center p-8 rounded-3xl max-w-sm w-full mx-4" style={glassStyles.card}>
            <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            
            {isbnMetadata ? (
              <>
                {isbnMetadata.coverImageUrl && (
                  <div className="mb-4">
                    <img
                      src={isbnMetadata.coverImageUrl}
                      alt={isbnMetadata.title}
                      className="w-28 h-auto mx-auto rounded-xl shadow-lg"
                    />
                  </div>
                )}
                <h2 className="text-xl font-bold text-gray-900 mb-1">{isbnMetadata.title}</h2>
                {isbnMetadata.author && (
                  <p className="text-emerald-700 mb-2">by {isbnMetadata.author}</p>
                )}
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">ISBN Found!</h2>
                <p className="text-emerald-700 font-mono text-lg">{scannedIsbn}</p>
              </>
            )}
            
            <p className="text-gray-400 text-sm mt-6 animate-pulse">
              Starting page scanner...
            </p>
          </div>
        </div>
      )}
      
      {/* ================================================================== */}
      {/* MANUAL ISBN ENTRY STATE */}
      {/* ================================================================== */}
      {phase === 'manual-isbn' && (
        <div className="absolute inset-0 z-[200] flex flex-col bg-gradient-to-br from-slate-50 via-white to-indigo-50">
          <div className="p-4 flex items-center justify-between" style={glassStyles.header}>
            <button
              onClick={() => setPhase('isbn-scanning')}
              className="p-2 rounded-xl bg-white/50 text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Enter ISBN</h1>
            <div className="w-10" />
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <div className="w-full max-w-sm">
              {error && (
                <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200">
                  <div className="flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                </div>
              )}
              
              <input
                type="text"
                value={manualIsbn}
                onChange={(e) => {
                  setManualIsbn(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleManualIsbnSubmit()}
                placeholder="978-0-123456-78-9"
                className="w-full px-5 py-4 rounded-xl text-gray-900 text-lg font-mono tracking-wider text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white border border-gray-200"
                maxLength={25}
                autoFocus
              />
              
              <button
                onClick={handleManualIsbnSubmit}
                disabled={!manualIsbn.trim()}
                className="w-full mt-4 py-4 bg-indigo-600 text-white rounded-xl font-semibold disabled:opacity-50"
              >
                Look Up Book
              </button>
              
              <button
                onClick={handleSkipIsbn}
                className="w-full mt-4 py-3 text-gray-500"
              >
                No ISBN? Enter book title manually →
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* ISBN Scanner Region - always in DOM for runtime fallback when native fails */}
      {isIsbnPhase && (
        <div id="isbn-scanner-region" className={`absolute inset-0 z-[5] [&_video]:!object-cover [&>div]:!bg-transparent [&_*]:!border-none ${usingFallbackScanner ? '' : 'hidden'}`} />
      )}
      
      {/* Adjustable Frame Overlay (page scanning only) */}
      {isPagePhase && (
        <AdjustableFrame
          corners={frameCorners}
          onCornersChange={setFrameCorners}
          preset={framePreset}
          onPresetChange={setFramePreset}
          containerWidth={videoDimensions.width}
          containerHeight={videoDimensions.height}
          showPresets={showFramePresets}
          locked={phase !== 'page-scanning'}
          maskOpacity={0.4}
        />
      )}
      
      {/* Capture Flash */}
      {phase === 'captured' && (
        <div className="absolute inset-0 z-[200] pointer-events-none">
          <div
            className="absolute inset-0 border-[12px] border-green-500 rounded-lg"
            style={{ animation: 'borderFlash 0.3s ease-out forwards' }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="flex items-center gap-2 bg-green-500 px-4 py-2 rounded-full shadow-lg">
              <CheckCircle className="w-5 h-5 text-white" />
              <span className="text-white font-semibold">Page {capturedPageNum}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* ================================================================== */}
      {/* ACTIVE SCANNING UI (ISBN or Page scanning) */}
      {/* ================================================================== */}
      {(isIsbnPhase || isPagePhase) && (
        <>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-[80] p-3 bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(bookInfo?.coverImageUrl) ? (
              <img
                src={bookInfo.coverImageUrl}
                alt=""
                className="w-10 h-10 rounded-lg object-cover border border-white/30"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white/70" />
              </div>
            )}
            <div>
              <h3 className="text-white font-medium text-sm line-clamp-1">
                {isIsbnPhase ? 'Find ISBN Barcode' : (bookInfo?.title || 'Scanning Pages')}
              </h3>
              <p className="text-white/60 text-xs">
                {isIsbnPhase
                  ? 'Usually on back cover'
                  : `${scannedPages.length} pages captured`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Focus indicator (page scanning only) */}
            {isPagePhase && (
              <MinimalFocusIndicator
                focusStatus={autoCapture.focus.status}
                captureProgress={autoCapture.captureProgress}
              />
            )}
            
            {/* Frame settings toggle (page scanning only) */}
            {isPagePhase && (
              <button
                onClick={() => setShowFramePresets(!showFramePresets)}
                className={`p-2 rounded-full ${showFramePresets ? 'bg-white/30' : 'bg-black/30'}`}
              >
                <Settings2 className="w-5 h-5 text-white" />
              </button>
            )}
            
            {/* Done button (page scanning only) */}
            {isPagePhase && scannedPages.length >= 1 && (
              <button
                onClick={handleDone}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium"
              >
                Done
              </button>
            )}
            
            {/* Close button */}
            <button
              onClick={handleClose}
              className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
        
        {/* Page dots (page scanning only) */}
        {isPagePhase && scannedPages.length > 0 && (
          <div className="flex gap-1.5 mt-3 justify-center flex-wrap max-w-[80%] mx-auto">
            {scannedPages.slice(-20).map(p => (
              <div
                key={p}
                className="w-2.5 h-2.5 rounded-full bg-green-500"
                title={`Page ${p}`}
              />
            ))}
            {phase === 'page-scanning' && (
              <div className="w-2.5 h-2.5 rounded-full bg-white/40 animate-pulse" />
            )}
          </div>
        )}
      </div>
      
      {/* ISBN Scanning UI - Simplified for seamless scanning */}
      {isIsbnPhase && (
        <>
          {/* Minimal scan frame - just corners, no box (html5-qrcode handles detection area) */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-72 h-40 relative">
              {/* Corner accents only - minimal visual noise */}
              <div className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-3 border-l-3 rounded-tl-lg" style={{ borderColor: colors.brand.primary }} />
              <div className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-3 border-r-3 rounded-tr-lg" style={{ borderColor: colors.brand.primary }} />
              <div className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-3 border-l-3 rounded-bl-lg" style={{ borderColor: colors.brand.primary }} />
              <div className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-3 border-r-3 rounded-br-lg" style={{ borderColor: colors.brand.primary }} />
            </div>
          </div>
          
          {/* Footer - compact */}
          <div className="absolute bottom-0 left-0 right-0 z-[80] p-4 pb-6 bg-gradient-to-t from-black/70 to-transparent">
            <div className="max-w-lg mx-auto">
              <div className="flex gap-2">
                <button
                  onClick={() => setPhase('manual-isbn')}
                  className="flex-1 px-3 py-2.5 bg-white/20 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Keyboard className="w-4 h-4" />
                  Type ISBN
                </button>
                <button
                  onClick={handleSkipIsbn}
                  className="flex-1 px-3 py-2.5 bg-white/10 text-white/80 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                  Skip
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Page Scanning UI */}
      {isPagePhase && (
        <>
          {/* Guidance */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-[100] flex flex-col items-center pointer-events-none">
            {phase === 'page-scanning' && (
              <div className="bg-black/50 backdrop-blur-sm px-5 py-3 rounded-xl">
                <div className="text-white/90 text-lg">{autoCapture.guidance}</div>
              </div>
            )}
            {phase === 'cooldown' && (
              <div className="bg-amber-500/80 backdrop-blur-sm px-5 py-3 rounded-xl">
                <div className="text-white text-lg font-medium">Turn the page...</div>
              </div>
            )}
          </div>
          
          {/* Footer with manual capture */}
          <div className="absolute bottom-0 left-0 right-0 z-[80] p-4 pb-8 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-center">
              <button
                onClick={handleManualCapture}
                disabled={isProcessing || phase !== 'page-scanning'}
                className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-xl active:scale-95 transition-transform disabled:opacity-50"
              >
                <Camera className="w-10 h-10 text-gray-800" />
              </button>
            </div>
            <p className="text-white/50 text-xs text-center mt-3">
              Tap to capture • Auto-captures when steady
            </p>
          </div>
        </>
      )}
        </>
      )}
      
      {/* CSS for animations */}
      <style>{`
        @keyframes borderFlash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default UnifiedScanner;